import { omit } from 'lodash';
import { Promise as BluebirdPromise } from 'bluebird';
import { FILE_STATES } from '../../constants';
import { postAttachment } from '../../api/uploadApi';
import { handleUploadBackendError } from '../../validations/backendErrors';

const { UPLOADED, PENDING, FAILED, INVALID } = FILE_STATES;

const ADD_FILES = 'efes/upload/ADD_FILES';
const REMOVE_FILE = 'efes/upload/REMOVE_FILE';
const ADD_RESPONSE = 'efes/upload/ADD_RESPONSE';
const SET_FILE_STATE = 'efes/upload/SET_FILE_STATE';
const SET_FILE_ERROR = 'efes/upload/SET_FILE_ERROR';
const SET_IS_UPLOADING = 'efes/upload/SET_IS_UPLOADING';

const initialState = {
  files: [],
  responses: [],
  fileStates: {},
  errors: {},
  isUploading: false,
};

export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case ADD_FILES:
      return {
        ...state,
        files: [].concat(state.files).concat(action.files),
      };
    case REMOVE_FILE:
      return {
        ...state,
        files: state.files.filter((f) => f.src !== action.fileSrc),
        fileStates: omit(state.fileStates, action.fileSrc),
        errors: omit(state.errors, action.fileSrc),
      };
    case ADD_RESPONSE:
      return {
        ...state,
        responses: [...state.responses, action.response],
      };
    case SET_FILE_STATE:
      return {
        ...state,
        fileStates: {
          ...state.fileStates,
          [action.fileSrc]: action.fileState,
        },
      };
    case SET_FILE_ERROR:
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.fileSrc]: action.error,
        },
      };

    case SET_IS_UPLOADING:
      return {
        ...state,
        isUploading: action.value,
      };
    default:
      return state;
  }
}

export const createUploadReducerWithName = (name) => (
  state = initialState,
  action = {},
) => {
  if (action.name !== name) {
    return state;
  }
  return reducer(state, action);
};

export const getActionCreatorsWithName = (name) => {
  const addFiles = (files) => ({ type: ADD_FILES, files, name });
  const removeFile = (fileSrc) => ({ type: REMOVE_FILE, fileSrc, name });
  const addResponse = (response) => ({ type: ADD_RESPONSE, response, name });
  const setFileState = (fileSrc, fileState) => ({
    type: SET_FILE_STATE,
    fileSrc,
    fileState,
    name,
  });
  const setFileError = (fileSrc, error) => ({
    type: SET_FILE_ERROR,
    fileSrc,
    error,
    name,
  });
  const setIsUploading = (value) => ({ type: SET_IS_UPLOADING, value, name });

  const uploadFiles = (
    fileData,
    executeRecaptcha = () => Promise.resolve(),
  ) => (dispatch, getState) => {
    const { files, fileStates } = fileData;
    dispatch(setIsUploading(true));
    return BluebirdPromise.each(
      files, (f) => {
        const isUploaded = fileStates[f.src] === UPLOADED;
        const isPending = fileStates[f.src] === PENDING;
        const isInvalid = fileStates[f.src] === INVALID;
        if (isUploaded || isPending || isInvalid) {
          return null;
        }

        let uploadStarted;

        dispatch(setFileState(f.src, PENDING));
        return executeRecaptcha('upload')
          .then((recaptchaToken) => {
            uploadStarted = Date.now();
            return postAttachment(f.data, recaptchaToken);
          })
          .then((response) => {
            dispatch(setFileState(f.src, UPLOADED));
            dispatch(addResponse({ type: f.type, uuid: response.uuid }));
          })
          .catch((error) => {
            const durationInMs = Date.now() - uploadStarted;
            const currentAttachmentsState = getState()[name].attachments;
            // name is either cmtplForm or personalClaim

            currentAttachmentsState.durationInMs = durationInMs;
            const backendError = handleUploadBackendError(error, currentAttachmentsState);
            dispatch(setFileState(f.src, FAILED));
            dispatch(setFileError(f.src, backendError));
            throw error;
          });
      },
    ).then(() => {
      dispatch(setIsUploading(false));
    })
      .catch((error) => {
        files.forEach((f) => {
          const fileState = fileStates[f.src];
          if (fileState === PENDING) {
            dispatch(setFileState(f.src, null));
          }
        });
        dispatch(setIsUploading(false));
        throw error;
      });
  };

  return {
    addFiles,
    removeFile,
    addResponse,
    setFileState,
    setFileError,
    uploadFiles,
  };
};
